import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { RecordBillPaymentInput } from '@accounting/shared';
import { useBill, useRecordBillPayment, useVoidBill } from '../../hooks/useBills';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AttachmentsPanel } from '../../components/attachments/AttachmentsPanel';
import { ApiError } from '../../lib/api';
import { withDuplicateConfirm } from '../../lib/duplicateConfirm';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import { billStatusTag } from './bills.index';

export const Route = createFileRoute('/_auth/bills/$id')({
  component: BillDetailPage,
});

function BillDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: bill, isLoading, error } = useBill(companyId, id);
  const voidBill = useVoidBill(companyId);
  const [payOpen, setPayOpen] = useState(false);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !bill) return <p className="field-error">Failed to load bill.</p>;

  const voidError =
    voidBill.error instanceof ApiError
      ? (voidBill.error.body as { error?: string } | null)?.error ?? 'Void failed'
      : voidBill.error?.message;

  const canPay = bill.status !== 'void' && parseFloat(bill.outstandingAmount) > 0;

  return (
    <div>
      <Link to="/bills" style={{ fontSize: 13 }}>← Bills</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Bill {bill.billNumber} <span style={{ marginLeft: 8 }}>{billStatusTag(bill)}</span>
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {canPay && (
            <button className="btn-primary" onClick={() => setPayOpen(true)}>Pay bill</button>
          )}
          {bill.status !== 'void' && (
            <Link to="/bills/$id/edit" params={{ id: bill.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
              Edit
            </Link>
          )}
          {bill.status !== 'void' && (
            <button
              className="btn-danger"
              disabled={voidBill.isPending}
              onClick={() => {
                const reason = prompt('Reason for voiding this bill?');
                if (reason && reason.trim()) voidBill.mutate({ id: bill.id, reason: reason.trim() });
              }}
            >
              Void
            </button>
          )}
        </div>
      </div>
      {voidError && <div className="field-error" style={{ marginBottom: 12 }}>{voidError}</div>}

      <div className="card" style={{ padding: 16, maxWidth: 820, marginBottom: 16 }}>
        <div className="form-grid" style={{ marginBottom: 8 }}>
          <span className="muted">Vendor</span>
          <strong>{bill.vendorName}</strong>
          {bill.vendorReference && (<><span className="muted">Vendor ref #</span><span>{bill.vendorReference}</span></>)}
          <span className="muted">Bill date</span>
          <span>{formatAccountingDate(bill.billDate)}</span>
          <span className="muted">Due date</span>
          <span>{formatAccountingDate(bill.dueDate)}{bill.isOverdue ? ' (past due)' : ''}</span>
          {bill.terms && (<><span className="muted">Terms</span><span>{bill.terms}</span></>)}
          {bill.memo && (<><span className="muted">Memo</span><span>{bill.memo}</span></>)}
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lines.map((l) => (
              <tr key={l.id}>
                <td>{l.accountCode} — {l.accountName}</td>
                <td className="muted">{l.description ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{l.quantity}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.unitPrice)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={4}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(bill.total)}</td>
            </tr>
            <tr>
              <td colSpan={4} className="muted">Paid</td>
              <td style={{ textAlign: 'right' }} className="muted">{formatCurrency(bill.amountPaid)}</td>
            </tr>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4}>Balance due</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(bill.outstandingAmount)}</td>
            </tr>
          </tfoot>
        </table>

        {bill.journalEntryId && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Posted as <Link to="/journal/$id" params={{ id: bill.journalEntryId }}>journal entry</Link>.
            {bill.status === 'void' && bill.voidReason && ` Voided: ${bill.voidReason}.`}
          </p>
        )}
      </div>

      {bill.payments.length > 0 && (
        <div className="card" style={{ padding: 16, maxWidth: 820, marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Payments made</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Paid from</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {bill.payments.map((p) => (
                <tr key={p.id}>
                  <td>{formatAccountingDate(p.paymentDate)}</td>
                  <td className="muted">{p.paymentAccountCode} — {p.paymentAccountName}</td>
                  <td className="muted">{p.reference ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AttachmentsPanel companyId={companyId} entityType="bill" entityId={bill.id} />

      {payOpen && (
        <PayBillModal
          companyId={companyId}
          billId={bill.id}
          outstanding={bill.outstandingAmount}
          onClose={() => setPayOpen(false)}
        />
      )}
    </div>
  );
}

function PayBillModal({
  companyId,
  billId,
  outstanding,
  onClose,
}: {
  companyId: string;
  billId: string;
  outstanding: string;
  onClose: () => void;
}) {
  const pay = useRecordBillPayment(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [amount, setAmount] = useState(outstanding);
  const [reference, setReference] = useState('');
  const [paymentAccountId, setPaymentAccountId] = useState('');

  // Pay-from accounts: bank/cash assets and credit-card liabilities. Fall back to any
  // posting asset if no preferred-subtype accounts exist on this CoA.
  const hasPreferred = accounts.some(
    (a) =>
      a.isActive &&
      a.allowDirectPosting &&
      ((a.accountType === 'asset' && (a.accountSubtype === 'bank' || a.accountSubtype === 'other_current_asset')) ||
        (a.accountType === 'liability' && a.accountSubtype === 'credit_card')),
  );
  const isPayFromSelectable = (a: typeof accounts[number]) => {
    if (!a.isActive || !a.allowDirectPosting) return false;
    return hasPreferred
      ? (a.accountType === 'asset' && (a.accountSubtype === 'bank' || a.accountSubtype === 'other_current_asset')) ||
          (a.accountType === 'liability' && a.accountSubtype === 'credit_card')
      : a.accountType === 'asset';
  };

  const errorMessage =
    pay.error instanceof ApiError
      ? (pay.error.body as { error?: string } | null)?.error ?? 'Payment failed'
      : pay.error?.message;

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 10 } as const;

  const submit = () => {
    const input: RecordBillPaymentInput = {
      paymentDate,
      amount: String(parseFloat(amount) || 0),
      reference: reference.trim() || undefined,
      paymentAccountId,
    };
    // Ask once if an identical payment against this bill already exists.
    void withDuplicateConfirm(input, (i) =>
      pay.mutateAsync({ id: billId, input: i }),
    ).then((r) => {
      if (r) onClose();
    });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Pay bill</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Posts to Accounts Payable (debit) against the pay-from account (credit).
          Balance due: {formatCurrency(outstanding)}.
        </p>

        <label style={labelStyle} htmlFor="b-acct">Pay from</label>
        <AccountPicker
          id="b-acct"
          accounts={accounts}
          value={paymentAccountId}
          onChange={setPaymentAccountId}
          predicate={isPayFromSelectable}
        />

        <label style={labelStyle} htmlFor="b-date">Payment date</label>
        <input id="b-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} style={{ width: '100%' }} />

        <label style={labelStyle} htmlFor="b-amt">Amount</label>
        <input id="b-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />

        <label style={labelStyle} htmlFor="b-ref">Reference</label>
        <input id="b-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="check # / confirmation (optional)" style={{ width: '100%' }} />

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={pay.isPending || !paymentAccountId || !(parseFloat(amount) > 0)}>
            {pay.isPending ? 'Posting…' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
